import React, { useEffect, useState } from 'react';
import { extractError } from '../context/AuthContext';
import { notifAPI } from '../services/api';
import { fmtRelative } from '../utils/helpers';
import {
  Bell, Check, CheckCheck, Briefcase, UserCheck,
  Star, FileText, Clock, AlertCircle, Mail, Phone,
  ChevronDown, ChevronUp, Users, Play, Award,
  Trash2, RotateCcw, ArrowRightLeft, Activity,
} from 'lucide-react';
import toast from 'react-hot-toast';
import './Notifications.css';

const TYPE_MAP = {
  NOUVEL_AO:            { icon: Briefcase,       color: '#FF4100', bg: '#EEF2FF', label: 'Nouvel AO' },
  AO_AFFECTE:           { icon: UserCheck,        color: '#0891b2', bg: 'rgba(8,145,178,0.10)', label: 'AO Affecté' },
  AO_PRIS_EN_CHARGE:    { icon: Play,             color: '#f05a1a', bg: '#FFF7ED', label: 'Prise en charge' },
  SOURCING_DEMARRE:     { icon: Users,            color: '#d97706', bg: '#FFFBEB', label: 'Sourcing' },
  RAPPORT_PREQUAL_SOFT: { icon: FileText,         color: '#0891b2', bg: 'rgba(8,145,178,0.10)', label: 'Rapport Soft' },
  RAPPORT_PREQUAL_HARD: { icon: FileText,         color: '#7c3aed', bg: 'rgba(139,92,246,0.10)', label: 'Rapport Hard' },
  RAPPORT_APPEL:        { icon: Phone,            color: '#059669', bg: '#ECFDF5', label: 'Compte-rendu' },
  EMAIL_ENVOYE:         { icon: Mail,             color: 'var(--text-muted)', bg: '#F9FAFB', label: 'Email' },
  CANDIDAT_PIPE:        { icon: Award,            color: '#7c3aed', bg: 'rgba(139,92,246,0.10)', label: 'Pipeline' },
  ALERTE_DEADLINE:      { icon: Clock,            color: '#dc2626', bg: 'rgba(239,68,68,0.12)', label: 'Alerte délai' },
  SHORTLIST_VALIDEE:    { icon: Star,             color: '#f05a1a', bg: '#FFF7ED', label: 'Shortlist' },
  TOP_PROFIL_VALIDE:    { icon: Star,             color: '#FF4100', bg: '#EEF2FF', label: 'Top Profil' },
  AO_STATUT_CHANGE:     { icon: ArrowRightLeft,   color: '#0891b2', bg: 'rgba(8,145,178,0.10)', label: 'Statut AO' },
  AO_CORBEILLE:         { icon: Trash2,           color: '#9ca3af', bg: '#F9FAFB', label: 'Corbeille' },
  AO_RESTAURE:          { icon: RotateCcw,        color: '#059669', bg: '#ECFDF5', label: 'Restauré' },
  AO_SUPPRIME:          { icon: Trash2,           color: '#dc2626', bg: 'rgba(239,68,68,0.12)', label: 'Supprimé' },
  ACTION_CANDIDAT:      { icon: Activity,         color: '#7c3aed', bg: 'rgba(139,92,246,0.10)', label: 'Action' },
  default:              { icon: AlertCircle,      color: 'var(--text-muted)', bg: '#F9FAFB', label: 'Info' },
};

const PRIORITY_MAP = {
  URGENT: { label: 'Urgent', color: '#dc2626', bg: 'rgba(239,68,68,0.12)' },
  HIGH:   { label: 'Haute',  color: '#f05a1a', bg: '#FFF7ED' },
  NORMAL: null,
  LOW:    null,
};

function conf(type) {
  return TYPE_MAP[type] || TYPE_MAP.default;
}

function NotifCard({ n, onMarkRead }) {
  const [expanded, setExpanded] = useState(false);
  const c = conf(n.notif_type);
  const Icon = c.icon;
  const prio = PRIORITY_MAP[n.priority];
  const reportHtml = n.extra_data?.report_html;
  const aoLabel    = n.extra_data?.ao_label;
  const nextStage  = n.extra_data?.next_stage;
  const newStatusLabel = n.extra_data?.new_status_label;
  const isHtml = reportHtml && /<[a-z][\s\S]*>/i.test(reportHtml);

  return (
    <div
      className={`notif-card${!n.lu ? ' unread' : ''}`}
      style={!n.lu ? { borderLeftColor: c.color } : {}}
    >
      {/* Icône */}
      <div className="notif-icon-wrap" style={{ background: c.bg, color: c.color }}>
        <Icon size={16} />
      </div>

      {/* Corps */}
      <div className="notif-body">
        <div className="notif-top-row">
          <div className="notif-message">{n.message}</div>
          {prio && (
            <span className="notif-prio-badge" style={{ background: prio.bg, color: prio.color }}>
              {prio.label}
            </span>
          )}
        </div>

        <div className="notif-meta">
          <span className="notif-type-badge" style={{ background: c.bg, color: c.color }}>
            {c.label}
          </span>
          {aoLabel && <span className="notif-ao-label">{aoLabel}</span>}
          {nextStage && (
            <span className="notif-next-stage">
              &rarr; <strong style={{ color: c.color, marginLeft: 2 }}>{nextStage}</strong>
            </span>
          )}
          {newStatusLabel && !nextStage && (
            <span className="notif-next-stage">
              &rarr; <strong style={{ color: c.color, marginLeft: 2 }}>{newStatusLabel}</strong>
            </span>
          )}
          <span className="notif-time">{fmtRelative(n.created_at)}</span>
        </div>

        {reportHtml && (
          <>
            <button
              type="button"
              className="notif-report-toggle"
              style={{ color: c.color }}
              onClick={() => setExpanded(v => !v)}
            >
              {expanded ? <ChevronUp size={13} /> : <ChevronDown size={13} />}
              {expanded ? 'Masquer le rapport' : 'Voir le rapport'}
            </button>
            {expanded && (
              <div
                className="notif-report-body"
                style={{ background: c.bg, borderColor: c.color + '33' }}
              >
                {isHtml
                  ? <div className="rte-content" style={{ background: 'transparent', padding: 0 }} dangerouslySetInnerHTML={{ __html: reportHtml }} />
                  : <p style={{ margin: 0, whiteSpace: 'pre-wrap' }}>{reportHtml}</p>
                }
              </div>
            )}
          </>
        )}
      </div>

      {/* Bouton Lu */}
      <div className="notif-side">
        {!n.lu ? (
          <button className="notif-read-btn" onClick={() => onMarkRead(n.id)} title="Marquer comme lu">
            <Check size={13} style={{ marginRight: 4 }} />Lu
          </button>
        ) : (
          <span className="notif-done-badge">
            <CheckCheck size={12} /> Lu
          </span>
        )}
      </div>
    </div>
  );
}

export default function Notifications() {
  const [notifs,  setNotifs]  = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter,  setFilter]  = useState('all');

  const fetchNotifs = async () => {
    setLoading(true);
    try {
      const res = await notifAPI.list();
      setNotifs(res.data || []);
    } catch {
      setNotifs([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchNotifs(); }, []);

  const markRead = async (id) => {
    try {
      await notifAPI.markRead(id);
      setNotifs(n => n.map(x => x.id === id ? { ...x, lu: true } : x));
    } catch { toast.error('Erreur'); }
  };

  const markAllRead = async () => {
    try {
      await notifAPI.markAllRead();
      setNotifs(n => n.map(x => ({ ...x, lu: true })));
      toast.success('Toutes les notifications marquées comme lues');
    } catch { toast.error('Erreur'); }
  };

  const unreadCount = notifs.filter(n => !n.lu).length;
  const filtered = notifs.filter(n =>
    filter === 'unread' ? !n.lu : filter === 'read' ? n.lu : true
  );

  return (
    <div>
      <div className="page-header">
        <div className="page-header-left">
          <h1>Notifications</h1>
          <p style={{ color: 'var(--text-muted)', fontSize: '0.875rem', marginTop: 2 }}>
            {unreadCount > 0
              ? `${unreadCount} non lue${unreadCount > 1 ? 's' : ''}`
              : 'Tout est lu'}
          </p>
        </div>
        <div className="page-header-actions">
          {unreadCount > 0 && (
            <button className="btn btn-secondary" onClick={markAllRead}>
              <CheckCheck size={15} /> Tout marquer comme lu
            </button>
          )}
          <button className="btn btn-ghost btn-sm" onClick={fetchNotifs}>
            Actualiser
          </button>
        </div>
      </div>

      <div className="notif-tabs">
        {[
          { k: 'all',    label: 'Toutes',   count: notifs.length },
          { k: 'unread', label: 'Non lues', count: unreadCount },
          { k: 'read',   label: 'Lues',     count: notifs.length - unreadCount },
        ].map(({ k, label, count }) => (
          <button key={k} className={`notif-tab${filter === k ? ' active' : ''}`} onClick={() => setFilter(k)}>
            {label}
            <span
              className="notif-tab-count"
              style={{
                background: filter === k ? 'var(--blue-600)' : 'var(--gray-300)',
                color: filter === k ? 'white' : 'var(--text-muted)',
              }}
            >
              {count}
            </span>
          </button>
        ))}
      </div>

      {loading ? (
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, color: 'var(--text-muted)', padding: '40px 0' }}>
          <div className="spinner" /> Chargement...
        </div>
      ) : filtered.length === 0 ? (
        <div className="notif-empty">
          <Bell size={36} strokeWidth={1.5} />
          <h4>Aucune notification</h4>
          <p>Vous êtes à jour.</p>
        </div>
      ) : (
        <div className="notif-list">
          {filtered.map(n => (
            <NotifCard key={n.id} n={n} onMarkRead={markRead} />
          ))}
        </div>
      )}
    </div>
  );
}
